import { useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  Grid,
  Skeleton,
  Space,
  Statistic,
  Table,
  Typography,
  type TableProps,
} from 'antd';
import {
  AuditOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  FallOutlined,
  FileExcelOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { parseServerSorter, serverSortProps, type ServerSortState } from '../../lib/server-sort';
import { PageHeader } from '../../components/PageHeader';
import { DateRangeFilter, type DateRangeValue } from '../../components/DateRangeFilter';
import { DATE_FORMAT } from '../../lib/date';
import { downloadBlob } from '../../lib/download';
import { useT, i18nInstance } from '../../locales';
import {
  useComisionesDetail,
  useComisionesList,
  useComisionesTotal,
  useExportComisiones,
  useExportComisionesUser,
} from './api/useComisiones';
import { formatCurrency, type ComisionPagoDetalle, type ComisionResponse } from '@aid/shared';
import './comisiones.css';

const PAGE_SIZE = 20;
const COMISIONES_DATE_IDS = {
  start: 'comisiones-date-from',
  end: 'comisiones-date-to',
} as const;

function formatPaymentDate(value: string): string {
  return new Intl.DateTimeFormat(i18nInstance.language === 'en' ? 'en-US' : 'es-GT', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Guatemala',
  }).format(new Date(value));
}

function rateLabel(rate: string | null): string {
  return rate === null ? '—' : `${rate}%`;
}

function periodFeedback(t: ReturnType<typeof useT>, dateRange: DateRangeValue | null): string {
  const from = dateRange?.[0] ?? null;
  const to = dateRange?.[1] ?? null;
  const fmt = (value: string) => dayjs(value).format(DATE_FORMAT);
  if (from && to) return t('pages.comisiones.periodFull', { from: fmt(from), to: fmt(to) });
  if (from) return t('pages.comisiones.periodFrom', { from: fmt(from) });
  if (to) return t('pages.comisiones.periodTo', { to: fmt(to) });
  return t('pages.comisiones.periodAll');
}

function PaymentBreakdown({
  payment,
  t,
}: {
  payment: ComisionPagoDetalle;
  t: ReturnType<typeof useT>;
}) {
  return (
    <>
      <div
        className="comisiones-detail__metadata"
        aria-label={t('pages.comisiones.detailPayment', { id: payment.pagoId })}
      >
        <div>
          <span>{t('pages.comisiones.paymentDate')}</span>
          <strong>{formatPaymentDate(payment.fechaPago)}</strong>
        </div>
        <div>
          <span>{t('pages.comisiones.paymentAmount')}</span>
          <strong>{formatCurrency(payment.montoPago)}</strong>
        </div>
        <div>
          <span>{t('pages.comisiones.commissionBase')}</span>
          <strong>{formatCurrency(payment.baseComision)}</strong>
        </div>
        <div>
          <span>{t('pages.comisiones.commission')}</span>
          <strong>{formatCurrency(payment.comision)}</strong>
        </div>
        <div>
          <span>{t('pages.comisiones.rate')}</span>
          <strong>{rateLabel(payment.tasaEfectivaPct)}</strong>
        </div>
      </div>
      <section
        className="comisiones-detail__breakdown"
        aria-labelledby={`payment-${payment.pagoId}-breakdown`}
      >
        <Typography.Title level={5} id={`payment-${payment.pagoId}-breakdown`}>
          {t('pages.comisiones.serviceBreakdown')}
        </Typography.Title>
        {payment.servicios.length === 0 ? (
          <p className="comisiones-detail__services-empty">
            {t('pages.comisiones.noCommissionBase')}
          </p>
        ) : (
          <ul className="comisiones-detail__services">
            {payment.servicios.map((service) => (
              <li
                key={`${service.servicioId}-${service.planId}`}
                className="comisiones-detail__service"
              >
                <strong className="comisiones-detail__service-plan">{service.planNombre}</strong>
                <dl className="comisiones-detail__service-values">
                  <div>
                    <dt>{t('pages.comisiones.basePrice')}</dt>
                    <dd>{formatCurrency(service.precioBase)}</dd>
                  </div>
                  <div>
                    <dt>{t('pages.comisiones.planCommission')}</dt>
                    <dd>{formatCurrency(service.comisionPlan)}</dd>
                  </div>
                  <div>
                    <dt>{t('pages.comisiones.assignedCommission')}</dt>
                    <dd>{formatCurrency(service.comisionAsignada)}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

export function ComisionesPage() {
  const t = useT();
  const screens = Grid.useBreakpoint();
  // Detail drawer width: 75% on desktop (lg+), 80% on tablet (md), mobile unchanged.
  const detailDrawerSize = screens.lg ? '75%' : screens.md ? '80%' : 'min(100vw, 736px)';
  const [dateRange, setDateRange] = useState<DateRangeValue | null>(() => [
    dayjs().startOf('month').format('YYYY-MM-DD'),
    dayjs().endOf('month').format('YYYY-MM-DD'),
  ]);
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<ComisionResponse | null>(null);
  const [detailPage, setDetailPage] = useState(1);
  const [sort, setSort] = useState<ServerSortState>({});
  const [detailSort, setDetailSort] = useState<ServerSortState>({});
  const fechaDesde = dateRange?.[0] ?? undefined;
  const fechaHasta = dateRange?.[1] ?? undefined;

  const listQuery = useComisionesList({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    fechaDesde,
    fechaHasta,
    ...sort,
  });
  const totalQuery = useComisionesTotal({ fechaDesde, fechaHasta });
  const detailQuery = useComisionesDetail(selectedUser?.userId ?? null, {
    limit: PAGE_SIZE,
    offset: (detailPage - 1) * PAGE_SIZE,
    fechaDesde,
    fechaHasta,
    ...detailSort,
  });
  const exportReport = useExportComisiones();
  const exportUserReport = useExportComisionesUser();
  const anyExportRunning = exportReport.isPending || exportUserReport.isPending;

  const handleExport = async (): Promise<void> => {
    exportReport.reset();
    try {
      const blob = await exportReport.mutateAsync({ fechaDesde, fechaHasta });
      downloadBlob(blob, `comisiones-${fechaDesde ?? 'inicio'}_${fechaHasta ?? 'hoy'}.xlsx`);
    } catch {
      // The mutation keeps the error for the visible recovery alert.
    }
  };

  const handleExportUser = async (): Promise<void> => {
    if (!selectedUser) return;
    exportUserReport.reset();
    try {
      const blob = await exportUserReport.mutateAsync({
        userId: selectedUser.userId,
        filters: { fechaDesde, fechaHasta },
      });
      downloadBlob(
        blob,
        `comisiones-${selectedUser.userId}-${fechaDesde ?? 'inicio'}_${fechaHasta ?? 'hoy'}.xlsx`,
      );
    } catch {
      // The mutation keeps the error for the visible recovery alert.
    }
  };

  const handleDateRangeChange = (value: DateRangeValue | null) => {
    setDateRange(value);
    setPage(1);
    setDetailPage(1);
  };

  const tableColumns: ColumnsType<ComisionResponse> = [
    {
      title: t('pages.comisiones.userName'),
      dataIndex: 'userName',
      key: 'userName',
      ...serverSortProps('userName', sort),
      render: (value: string) => (
        <span className="comisiones-user-cell">
          <UserOutlined aria-hidden="true" />
          <strong>{value}</strong>
        </span>
      ),
    },
    {
      title: t('pages.comisiones.totalCobrado'),
      dataIndex: 'totalCobrado',
      key: 'totalCobrado',
      ...serverSortProps('totalCobrado', sort),
      align: 'right',
      render: (value: string) => formatCurrency(value),
    },
    {
      title: t('pages.comisiones.totalComision'),
      dataIndex: 'totalComision',
      key: 'totalComision',
      ...serverSortProps('totalComision', sort),
      align: 'right',
      render: (value: string) => <strong>{formatCurrency(value)}</strong>,
    },
    {
      title: t('pages.comisiones.serviciosCount'),
      dataIndex: 'serviciosCount',
      key: 'serviciosCount',
      ...serverSortProps('serviciosCount', sort),
      align: 'right',
    },
    {
      title: t('pages.comisiones.rate'),
      dataIndex: 'tasaEfectivaPct',
      key: 'rate',
      align: 'right',
      render: (value: string | null) => rateLabel(value),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'right',
      render: (_value, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          aria-label={t('pages.comisiones.viewDetailFor', { name: record.userName })}
          onClick={() => {
            setSelectedUser(record);
            setDetailPage(1);
          }}
        >
          {t('pages.comisiones.viewDetail')}
        </Button>
      ),
    },
  ];

  const summary = totalQuery.data;
  const detailColumns: ColumnsType<ComisionPagoDetalle> = [
    {
      title: t('pages.comisiones.paymentId'),
      dataIndex: 'pagoId',
      key: 'payment',
      ...serverSortProps('payment', detailSort),
      render: (value: number) => `#${value}`,
    },
    {
      title: t('pages.comisiones.paymentDate'),
      dataIndex: 'fechaPago',
      key: 'date',
      ...serverSortProps('date', detailSort),
      render: (value: string) => formatPaymentDate(value),
    },
    {
      title: t('pages.comisiones.client'),
      key: 'client',
      render: (_value, record) => record.cliente?.nombre ?? '—',
    },
    {
      title: t('pages.comisiones.paymentAmount'),
      dataIndex: 'montoPago',
      key: 'amount',
      ...serverSortProps('amount', detailSort),
      align: 'right',
      render: (value: string) => formatCurrency(value),
    },
    {
      title: t('pages.comisiones.commission'),
      dataIndex: 'comision',
      key: 'commission',
      ...serverSortProps('commission', detailSort),
      align: 'right',
      render: (value: string) => <strong>{formatCurrency(value)}</strong>,
    },
    {
      title: t('pages.comisiones.rate'),
      dataIndex: 'tasaEfectivaPct',
      key: 'rate',
      align: 'right',
      render: (value: string | null) => rateLabel(value),
    },
  ];

  const handleListChange: NonNullable<TableProps<ComisionResponse>['onChange']> = (
    pagination,
    _filters,
    sorter,
  ) => {
    const nextSort = parseServerSorter(sorter);
    if (nextSort.sortBy !== sort.sortBy || nextSort.sortOrder !== sort.sortOrder) {
      setSort(nextSort);
      setPage(1);
      return;
    }
    setPage(pagination.current ?? 1);
  };
  const handleDetailChange: NonNullable<TableProps<ComisionPagoDetalle>['onChange']> = (
    pagination,
    _filters,
    sorter,
  ) => {
    const nextSort = parseServerSorter(sorter);
    if (nextSort.sortBy !== detailSort.sortBy || nextSort.sortOrder !== detailSort.sortOrder) {
      setDetailSort(nextSort);
      setDetailPage(1);
      return;
    }
    setDetailPage(pagination.current ?? 1);
  };

  return (
    <div className="comisiones-page">
      <PageHeader
        title={t('pages.comisiones.title')}
        subtitle={t('pages.comisiones.subtitle')}
        actions={
          <Space
            wrap
            size={[8, 8]}
            role="group"
            aria-label={t('pages.comisiones.exportArea')}
          >
            <div className="comisiones-range" aria-label={t('pages.comisiones.rangeLabel')}>
              <Typography.Text type="secondary">
                {t('pages.comisiones.rangeLabel')}
              </Typography.Text>
              <div className="comisiones-range-fields">
                <label className="comisiones-visually-hidden" htmlFor={COMISIONES_DATE_IDS.start}>
                  {t('pages.comisiones.dateFromPlaceholder')}
                </label>
                <label className="comisiones-visually-hidden" htmlFor={COMISIONES_DATE_IDS.end}>
                  {t('pages.comisiones.dateToPlaceholder')}
                </label>
                <DateRangeFilter
                  id={COMISIONES_DATE_IDS}
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  placeholder={[
                    t('pages.comisiones.dateFromPlaceholder'),
                    t('pages.comisiones.dateToPlaceholder'),
                  ]}
                />
              </div>
            </div>
            <Button
              icon={<FileExcelOutlined aria-hidden="true" />}
              loading={exportReport.isPending}
              disabled={anyExportRunning}
              aria-label={t('pages.comisiones.exportExcel')}
              onClick={() => void handleExport()}
            >
              {t('pages.comisiones.exportExcel')}
            </Button>
          </Space>
        }
      />

      <Alert
        className="comisiones-period"
        type="info"
        showIcon
        title={t('pages.comisiones.periodTitle')}
        description={periodFeedback(t, dateRange)}
      />

      {(exportReport.isError || exportUserReport.isError) && (
        <Alert
          className="comisiones-export-error"
          type="error"
          showIcon
          closable
          title={t('pages.comisiones.exportError')}
          onClose={() => {
            exportReport.reset();
            exportUserReport.reset();
          }}
        />
      )}

      {totalQuery.isError ? (
        <Alert
          type="error"
          showIcon
          title={t('pages.comisiones.errorLoad')}
          action={
            <Button onClick={() => totalQuery.refetch()}>{t('pages.comisiones.retry')}</Button>
          }
        />
      ) : totalQuery.isLoading ? (
        <div className="comisiones-kpi-grid" aria-label={t('common.loading')}>
          {Array.from({ length: 4 }, (_, index) => (
            <Card key={index}>
              <Skeleton active paragraph={false} />
            </Card>
          ))}
        </div>
      ) : summary ? (
        <>
          <div className="comisiones-kpi-grid" aria-label={t('pages.comisiones.kpiCommission')}>
            <Card className="comisiones-kpi comisiones-kpi--commission">
              <Statistic
                title={t('pages.comisiones.kpiCommission')}
                value={formatCurrency(summary.totalComision)}
                prefix={<CalculatorOutlined />}
                styles={{ content: { fontWeight: 700 } }}
              />
            </Card>
            <Card className="comisiones-kpi">
              <Statistic
                title={t('pages.comisiones.kpiCollected')}
                value={formatCurrency(summary.totalCobrado)}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
            <Card className="comisiones-kpi">
              <Statistic
                title={t('pages.comisiones.kpiServices')}
                value={summary.serviciosCount}
                prefix={<AuditOutlined />}
              />
            </Card>
            <Card className="comisiones-kpi">
              <Statistic
                title={t('pages.comisiones.kpiRate')}
                value={rateLabel(summary.tasaEfectivaPct)}
                prefix={<FallOutlined />}
              />
            </Card>
          </div>
        </>
      ) : null}

      <Card className="comisiones-table-card" title={t('pages.comisiones.perUserTitle')}>
        {listQuery.isError ? (
          <Alert
            type="error"
            showIcon
            title={t('pages.comisiones.errorLoad')}
            action={
              <Button onClick={() => listQuery.refetch()}>{t('pages.comisiones.retry')}</Button>
            }
          />
        ) : listQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : !listQuery.data || listQuery.data.data.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <>
                <strong>{t('pages.comisiones.emptyTitle')}</strong>
                <span>{t('pages.comisiones.emptyDescription')}</span>
              </>
            }
          />
        ) : (
          <div
            className="comisiones-table-scroll"
            role="region"
            tabIndex={0}
            aria-label={t('pages.comisiones.perUserTitle')}
          >
            <Table<ComisionResponse>
              columns={tableColumns}
              dataSource={listQuery.data.data}
              rowKey="userId"
              scroll={{ x: 'max-content' }}
              onChange={handleListChange}
              pagination={
                listQuery.data.total > PAGE_SIZE
                  ? {
                      current: page,
                      pageSize: PAGE_SIZE,
                      total: listQuery.data.total,
                      showSizeChanger: false,
                      showTotal: (total, range) =>
                        t('pagination.showTotal', { from: range[0], to: range[1], total }),
                      onChange: setPage,
                    }
                  : false
              }
            />
          </div>
        )}
      </Card>

      <Drawer
        open={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        title={
          selectedUser
            ? t('pages.comisiones.detailTitle', { name: selectedUser.userName })
            : undefined
        }
        placement="right"
        size={detailDrawerSize}
        classNames={{ body: 'comisiones-detail-drawer__body' }}
        extra={
          selectedUser ? (
            <Space wrap size={[8, 8]}>
              <Button
                icon={<FileExcelOutlined aria-hidden="true" />}
                loading={exportUserReport.isPending}
                disabled={anyExportRunning}
                aria-label={t('pages.comisiones.exportDetailFor', { name: selectedUser.userName })}
                onClick={() => void handleExportUser()}
              >
                {t('pages.comisiones.exportExcel')}
              </Button>
              <Button onClick={() => setSelectedUser(null)}>
                {t('pages.comisiones.closeDetail')}
              </Button>
            </Space>
          ) : null
        }
      >
        {selectedUser && (
          <Typography.Paragraph type="secondary">
            {t('pages.comisiones.detailDescription')}
          </Typography.Paragraph>
        )}
        {detailQuery.isError ? (
          <Alert
            type="error"
            showIcon
            title={t('pages.comisiones.errorDetail')}
            action={
              <Button onClick={() => detailQuery.refetch()}>{t('pages.comisiones.retry')}</Button>
            }
          />
        ) : detailQuery.isLoading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : !detailQuery.data || detailQuery.data.data.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('pages.comisiones.detailEmpty')}
          />
        ) : (
          <div
            className="comisiones-table-scroll"
            role="region"
            tabIndex={0}
            aria-label={t('pages.comisiones.detailTitle', {
              name: selectedUser?.userName ?? '',
            })}
          >
            <Table<ComisionPagoDetalle>
              columns={detailColumns}
              dataSource={detailQuery.data.data}
              rowKey="pagoId"
              size="small"
              onChange={handleDetailChange}
              scroll={{ x: 'max-content' }}
              expandable={{
                fixed: true,
                expandedRowRender: (payment) => <PaymentBreakdown payment={payment} t={t} />,
              }}
              pagination={
                detailQuery.data.total > PAGE_SIZE
                  ? {
                      current: detailPage,
                      pageSize: PAGE_SIZE,
                      total: detailQuery.data.total,
                      showSizeChanger: false,
                      onChange: setDetailPage,
                    }
                  : false
              }
            />
          </div>
        )}
      </Drawer>
    </div>
  );
}
